import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { font } from '../../assets/common';
import positionProfileApi from '../../services/positionProfileApi';
import { get } from 'lodash';
import { useHistory } from 'react-router-dom';
import CustomAntModal from '../../components/CustomAntModal/CustomAntModal';

const ProfileGeneral = styled.section`
  font-family: ${font.inter};
  color: #3d3d3d;
  background-color: #fff;
  padding: 30px 24px;
  border-radius: 6px;
  display: flex;
  gap: 40px;
`;

const LeftProfile = styled.div`
  padding: 24px;
  background-color: #f4fbfb;
  width: 450px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 4px;
`;
const RightProfile = styled.div`
  width: 100%;
`;

const PositionName = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 29.05px;
  letter-spacing: 0.30000001192092896px;
  text-align: left;
`;

const PositionCategory = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.10000000149011612px;
  color: #007874;
  padding: 5px 10px;
  background-color: #e0f3f3;
`;

const SmallTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.10000000149011612px;
  color: #575757;
  margin-bottom: 5px;
`;

const NormalTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  color: #3d3d3d;
  margin-bottom: 5px;
`;

const SmallContent = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 24px;
  color: #3d3d3d;
  ${({ viewmore }) => {
    if (viewmore)
      return css`
        color: #00a19c;
        cursor: pointer;
        font-weight: 700;
      `;
  }}
`;

const NormalContent = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  color: #3d3d3d;
  ${({ link }) => {
    if (link)
      return css`
        color: #00a19c;
        text-decoration: underline;
        cursor: pointer;
      `;
  }}
`;

const BreakLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: #cbd6e2;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 256px);
  grid-auto-rows: 1fr;
  padding-bottom: 20px;
  gap: 10px;
  margin-top: 30px;
`;

const FirstSectionPosition = (props) => {
  const { position_code, roleActive, setBreadCrumb } = props;
  const [data, setData] = useState(null);
  const [viewmore, setViewmore] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (!position_code || !roleActive?.roleId) return;
    (async function getFirstSectionPosition() {
      try {
        const res = await positionProfileApi.getFirstSectionPosition({ position_code }, roleActive.roleId);
        setData(get(res, 'data.result'));
        const breadcrumb = get(res, 'data.result')?.position_name;
        if (breadcrumb) setBreadCrumb((prev) => [...prev, breadcrumb]);
      } catch (error) {
        console.log(error);
        setData(null);
      }
    })();
  }, [position_code, roleActive?.roleId]);

  return (
    <div>
      <ProfileGeneral>
        <LeftProfile>
          <div className="d-flex justify-content-between align-items-top" style={{ gap: '20px' }}>
            <PositionName>{data?.position_name}</PositionName>
            {data?.position_label && (
              <div>
                <PositionCategory>{data?.position_label}</PositionCategory>
              </div>
            )}
          </div>
          <div className="d-flex justify-content-start align-items-top" style={{ gap: '10px' }}>
            <div style={{ width: '100px' }}>
              <SmallTitle>Position Code</SmallTitle>
            </div>
            <NormalContent>{data?.position_code}</NormalContent>
          </div>
          <div className="d-flex justify-content-start align-items-top" style={{ gap: '10px' }}>
            <div style={{ width: '100px' }}>
              <SmallTitle>SP</SmallTitle>
            </div>
            <NormalContent
              link={data?.is_sp_available ? true : false}
              onClick={data?.is_sp_available ? () => history.push('/succession-planning-dashboard/' + data?.position_code) : undefined}
            >
              {data?.is_sp_available ? 'Available' : 'Not Available'}
            </NormalContent>
          </div>
          <BreakLine />
          <div>
            <SmallTitle>Job Grade</SmallTitle>
            <NormalContent>{data?.job_grade}</NormalContent>
          </div>
          <div>
            <SmallTitle>Equivalent Job Grade</SmallTitle>
            <NormalContent>{data?.equi_job_grade}</NormalContent>
          </div>
          <div>
            <SmallTitle>Estimated Job Grade</SmallTitle>
            <NormalContent>{data?.est_job_grade}</NormalContent>
          </div>
        </LeftProfile>
        <RightProfile>
          <div>
            <NormalTitle>Job Purpose:</NormalTitle>
            <SmallContent>
              {data?.description && (
                <>
                  <div dangerouslySetInnerHTML={{ __html: data?.description.slice(0, 660) }}></div>
                  {data?.description.length > 660 && (
                    <SmallContent viewmore={true} onClick={() => setViewmore(true)}>
                      ...View more
                    </SmallContent>
                  )}
                </>
              )}
            </SmallContent>
          </div>
          <Grid>
            <div>
              <SmallTitle>Business Unit</SmallTitle>
              <NormalContent>{data?.business_unit}</NormalContent>
            </div>
            <div>
              <SmallTitle>Sector</SmallTitle>
              <NormalContent>{data?.sector}</NormalContent>
            </div>
            <div>
              <SmallTitle>OPU</SmallTitle>
              <NormalContent>{data?.opu_name}</NormalContent>
            </div>
            <div>
              <SmallTitle>Division</SmallTitle>
              <NormalContent>{data?.division}</NormalContent>
            </div>
            <div>
              <SmallTitle>Department</SmallTitle>
              <NormalContent>{data?.department_name}</NormalContent>
            </div>
            <div>
              <SmallTitle>SKG</SmallTitle>
              <NormalContent>{data?.skg}</NormalContent>
            </div>
            <div>
              <SmallTitle>Job Family</SmallTitle>
              <NormalContent>{data?.job_family}</NormalContent>
            </div>
            <div>
              <SmallTitle>Sub Job Family</SmallTitle>
              <NormalContent>{data?.sub_job_family}</NormalContent>
            </div>
            <div>
              <SmallTitle>Job Function</SmallTitle>
              <NormalContent>{data?.job_function}</NormalContent>
            </div>
          </Grid>
        </RightProfile>
      </ProfileGeneral>
      {viewmore && (
        <CustomAntModal
          open={viewmore}
          setOpen={setViewmore}
          width={1000}
          centered
          styles={{
            borderRadius: 8,
            closePosition: 50,
            containerPaddingLeftRight: 50,
            containerPaddingTopBottom: 50,
          }}
          hideDefaultBtn
        >
          <div style={{ paddingTop: '100px' }}>
            <PositionName>Job Purpose:</PositionName>
            <SmallContent>
              <div style={{ marginTop: '20px' }} dangerouslySetInnerHTML={{ __html: data?.description }}></div>
            </SmallContent>
          </div>
        </CustomAntModal>
      )}
    </div>
  );
};

export default FirstSectionPosition;
